import React, { useEffect } from 'react'; 
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SignUp from './pages/SignIn/SignIn';  
import Landing from './pages/Landing/Landing';  
import AdministrationPage from './pages/AdministrationPage/AdministrationPage';
import MetricDictionaryPage from './pages/MetricDictionaryPage/MetricDictionaryPage';
import KnowledgeCenterPage from './pages/KnowledgeCenterPage/KnowledgeCenterPage';
import OutageManagementPage from './pages/OutageManagementPage/OutageManagementPage';
import LineView from './pages/LineView/LineViewPage';
import AssetDashboard from './pages/AssetDashboardPage/AssetDashboard';
import MarketMonitorVisualPage from './pages/MarketMonitorPage/MarketMonitorVisualPage';
import Downloads from './pages/Downloads/DownloadsPage';
import LegacyReportPage from './pages/LegacyReport/LegacyReportPage';
// import OperationsPage from './pages/OperationsPage/OperationsPage';
import OutagePage from './pages/OperationsPage/OutageChart';
import { DataProvider } from "./components/DataContext/PortfolioDataContext";
import { AssetProvider } from './components/DataContext/AssetContext';
import { EntitlementsProvider } from './components/DataContext/EntitlementsContext';
import { FileCenterProvider } from './components/DataContext/FileCenterContext';
import { ChildAppsProvider } from './shellHost/ChildAppsContext';
import ChildAppPage from './pages/ChildAppPage/ChildAppPage';
import "./global.d.ts"; 
import ScrollToTop from "../src/components/ScrollTop/ScrollToTop";
import ProtectedRoute from "../src/ProtectedRoute";
import Unauthorized from './pages/UnauthoriziedPage/Unauthorized';
import NotFound from './pages/NotFoundpage/NotFoundPage';

// Import Datadog Logs
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from '@datadog/browser-logs';

const msalInstance = new PublicClientApplication(msalConfig);

declare global {
  interface Window {
    __DATADOG_INITIALIZED__?: boolean;
  }
}

const App: React.FC = () => {

  useEffect(() => {
    const userData = localStorage.getItem("datadogUser");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        datadogRum.setUser(user);
        datadogLogs.setGlobalContext({
          userId: user.id,
          userName: user.name,
          userEmail: user.email,
        });
      } catch (err) {
        console.error("Failed to restore Datadog user context", err);
      }
    }
  }, []);
  
  
  
  useEffect(() => {
    if (window.__DATADOG_INITIALIZED__) return;
    window.__DATADOG_INITIALIZED__ = true;
  
    const runtimeConfig = {
      DD_API_KEY: process.env.REACT_APP_DD_API_KEY || '',
      DD_CUSTOM_METRICS_ENABLED: process.env.REACT_APP_DD_CUSTOM_METRICS_ENABLED || '',
      DD_ENV: process.env.REACT_APP_DD_ENV || '',
      DD_LOGS_DIRECT_SUBMISSION_INTEGRATIONS: process.env.REACT_APP_DD_LOGS_DIRECT_SUBMISSION_INTEGRATIONS || '',
      DD_LOGS_INJECTION: process.env.REACT_APP_DD_LOGS_INJECTION || '',
      DD_PROFILING_ENABLED: process.env.REACT_APP_DD_PROFILING_ENABLED || '',
      DD_SERVICE: process.env.REACT_APP_DD_SERVICE || '',
      DD_SITE: process.env.REACT_APP_DD_SITE || 'us3.datadoghq.com', 
      DD_START_APP: process.env.REACT_APP_DD_START_APP || 'node ./bin/www',
      DD_VERSION: process.env.REACT_APP_DD_VERSION || '',
      DD_APPLICATION_ID: process.env.REACT_APP_DD_RUM_APPLICATION_ID || '',
      DD_API_TOKEN: process.env.REACT_APP_DD_RUM_CLIENT_TOKEN || '',
    };
  
    type DatadogSite = "datadoghq.com" | "datadoghq.eu" | "us3.datadoghq.com";
    const allowedSites: DatadogSite[] = ["datadoghq.com", "datadoghq.eu", "us3.datadoghq.com"];
    const site: DatadogSite = allowedSites.includes(runtimeConfig.DD_SITE as DatadogSite)
      ? (runtimeConfig.DD_SITE as DatadogSite)
      : "us3.datadoghq.com";
  
    datadogLogs.init({
      clientToken: runtimeConfig.DD_API_TOKEN,
      site,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error", "warn"],
      sessionSampleRate: 100,
      service: runtimeConfig.DD_SERVICE || 'amp-ui',
      env: runtimeConfig.DD_ENV || 'dev',
      version: runtimeConfig.DD_VERSION || '',
    });
  
    datadogRum.init({
      applicationId: runtimeConfig.DD_APPLICATION_ID,
      clientToken: runtimeConfig.DD_API_TOKEN,
      site,
      service: runtimeConfig.DD_SERVICE || 'amp',
      env: runtimeConfig.DD_ENV || 'dev',
      version: runtimeConfig.DD_VERSION || '',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'allow',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
    });
  
    if (["stg", "prd", "production","stage"].includes(runtimeConfig.DD_ENV.toLowerCase())) {
      datadogRum.startSessionReplayRecording();
    }
  }, []);
  
  
  function HealthCheck() {
    return <div>AMP Health Check is Ok</div>;
  }
  
  return (
    <MsalProvider instance={msalInstance}>
      <DataProvider>
        <AssetProvider>
          <EntitlementsProvider>
            <FileCenterProvider>
            <ChildAppsProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  <Route path="/" element={<SignUp />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="*" element={<NotFound />} />
                  <Route path="/health" element={<HealthCheck />} />

                  {/* Protected Routes */}
                  <Route path="/administration" element={<ProtectedRoute><AdministrationPage /></ProtectedRoute>} />
                  <Route path="/knowledge-center" element={<ProtectedRoute><KnowledgeCenterPage /></ProtectedRoute>} />
                  <Route path="/knowledge-center/metric" element={<ProtectedRoute><KnowledgeCenterPage /></ProtectedRoute>} />
                  <Route path="/knowledge-center/api-endpoints" element={<ProtectedRoute><KnowledgeCenterPage /></ProtectedRoute>} />
                  <Route path="/knowledge-center/user-guide" element={<ProtectedRoute><KnowledgeCenterPage /></ProtectedRoute>} />
                  <Route path="/metric-dictionary" element={<ProtectedRoute><MetricDictionaryPage /></ProtectedRoute>} />
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/lineview" element={<LineView />} />
                  <Route   path="/asset-dashboard/:tab/:assetSlug" element={<ProtectedRoute><AssetDashboard /></ProtectedRoute>} />
                  <Route path="/filecenter" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
                  <Route path="/custom-reports" element={<ProtectedRoute><LegacyReportPage /></ProtectedRoute>} />
                  <Route path="/outages" element={<ProtectedRoute><OutagePage /></ProtectedRoute>} />
                  {/* <Route path="/outage-management" element={<ProtectedRoute path="/outage-management"> <OutageManagementPage /> </ProtectedRoute>} /> */}
                  {/* <Route path="/outage-management" element={<OutageManagementPage />}/> */}
                  <Route path="/outage-management" element={<ProtectedRoute><OutageManagementPage /></ProtectedRoute>} />
                  <Route path="/market-fundamentals" element={<ProtectedRoute><MarketMonitorVisualPage /></ProtectedRoute>} />
                  <Route path="/apps/:slug/*" element={<ProtectedRoute><ChildAppPage /></ProtectedRoute>} />
                </Routes>
              </Router>
            </ChildAppsProvider>
            </FileCenterProvider>
          </EntitlementsProvider>
        </AssetProvider>
      </DataProvider>
  </MsalProvider>
  );
};

export default App;
